import { Alert, Button, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { composeValidators, required } from 'components/helpers/validators';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import { OrderFrequencyFields } from './OrderFrequencyFields';
import { OrderNotesField } from './OrderNotesField';
import { productService } from 'shared/services';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import {
  IdentityField,
  AutocompleteAdapter,
  TextFieldAdapter,
  DatePickerAdapter
} from 'components/enhanced';
import { SectionHeader } from 'components/ui/typography';

interface PurchaseRequestLineFormProps {
  onSubmit: any;
  products: Option[];
  setProduct: any;
  contracts: Option[];
  setContract: any;
  warehouses?: Option[];
  setWarehouse?: any;
  defaultWarehouse?: Option;
  disableWarehouse?: boolean;
  maxUnits: number;
  maxDiscountedUnits: number;
  automaticGenerated?: boolean;
}

const PurchaseRequestLineForm = ({
  onSubmit,
  products,
  setProduct,
  contracts,
  setContract,
  warehouses,
  setWarehouse,
  defaultWarehouse,
  disableWarehouse,
  maxUnits,
  maxDiscountedUnits,
  automaticGenerated
}: PurchaseRequestLineFormProps) => {
  const translationPrefix = 'pages.purchase-requests.new';
  const { t } = useTranslation();
  const [programmed, setProgrammed] = useState(false);
  const [selectedContractLine, setSelectedContractLine] = useState(); // Stato per contract line selezionata
  const [selectWarehouse, setSelectedWarehouse] = useState();

  const safeProducts = Array.isArray(products) ? products : [];
  const safeContracts = Array.isArray(contracts) ? contracts : [];
  const safeWarehouses = Array.isArray(warehouses) ? warehouses : [];

  const handleContractLineChange = (contractLine) => {
    setSelectedContractLine(contractLine);
    setContract(contractLine);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProgrammed(event.target.checked);
  };

  useEffect(() => {
    if (typeof setWarehouse === 'function') {
      setWarehouse(selectWarehouse);
    }
  }, [selectWarehouse]);

  const addBusinessDays = (date, days) => {
    let result = dayjs(date);
    let added = 0;
    while (added < days) {
      result = result.add(1, 'day');
      if (result.day() !== 0 && result.day() !== 6) {
        added++;
      }
    }
    return result;
  };

  return (
    <EnhancedForm
      onSubmit={(values) => {
        onSubmit({ ...values, contracts, programmed });
      }}
      formId="addPurchaseRequestLine"
      autoStyleLayout
      columns={2}
      costumControls={
        <Button
          type="submit"
          form="addPurchaseRequestLine"
          variant="outlined"
          className="w-full"
          style={{ gridColumn: '1 / -1' }}>
          {t(`${translationPrefix}.add_line`)}
        </Button>
      }>
      <div style={{ gridColumn: '1 / -1' }}>
        {automaticGenerated === false && safeWarehouses.length > 0 && (
          <div className="bg-gray-50 border border-gray-100 rounded-xl px-6 py-3 shadow-sm hover:shadow-md transition-shadow duration-200">
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={programmed}
                    onChange={handleChange}
                    color="primary"
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                }
                label={
                  <Typography variant="body2" className="text-sm font-semibold text-gray-700 ml-1">
                    <b>{t(`${translationPrefix}.order-programmed`)}</b>
                  </Typography>
                }
              />
            </FormGroup>
          </div>
        )}
      </div>
      {selectedContractLine && selectedContractLine['order_notes'] && (
        <div className="mb-4 w-full" style={{ gridColumn: '1 / -1' }}>
          <Alert severity="info" className="rounded-xl border-blue-100 bg-blue-50/30 text-blue-900">
            <SectionHeader title="Presenza Note: " subtitle={selectedContractLine['order_notes']} />
          </Alert>
        </div>
      )}
      {/* Dettagli prodotti */}
      <SectionHeader
        title={t(`${translationPrefix}.product-info`)}
        subtitle="Seleziona il magazzino di origine, il prodotto desiderato e associa la relativa linea di contratto."
        gridSpan
      />
      {safeWarehouses.length > 0 && (
        <IdentityField
          name="warehouse"
          component={AutocompleteAdapter}
          options={safeWarehouses}
          initValue={defaultWarehouse}
          disabled={disableWarehouse}
          label={t('global.warehouse')}
          placeholder="Warehouse"
          type="text"
          optionValue="code"
          validate={composeValidators(required)}
          setValue={setSelectedWarehouse}
          getMethodService={warehouseService}
          required
          submitObject
        />
      )}
      <IdentityField
        gridSpan={safeWarehouses.length > 0 ? 1 : 2}
        name="product"
        component={AutocompleteAdapter}
        options={safeProducts}
        label={t('global.product')}
        optionLabel="label"
        type="text"
        validate={composeValidators(required)}
        setValue={setProduct}
        getMethodService={productService}
        required
        submitObject
      />
      <IdentityField
        gridSpan={2}
        name="contract_line"
        component={AutocompleteAdapter}
        options={safeContracts}
        label={t('global.contract')}
        optionLabel="description"
        placeholder="Contract"
        type="text"
        validate={composeValidators(required)}
        setValue={handleContractLineChange}
        required
        autoSelect={true}
      />

      {/* Quantità da ordinare */}
      <SectionHeader
        title={t(`${translationPrefix}.stock-quantities`)}
        subtitle="Inserisci le quantità desiderate per l'ordine, verificando la disponibilità dei prodotti standard e scontati."
        gridSpan
      />
      <div>
        {maxUnits !== null && (
          <Typography variant="caption" className="text-sm font-medium text-gray-500">
            {t('entities.contract_line.available_units')}:{' '}
            <span className="font-semibold text-primary-600">{maxUnits}</span>
          </Typography>
        )}
        <IdentityField
          name="quantity"
          component={TextFieldAdapter}
          label={t('global.quantity')}
          placeholder={t('global.quantity')}
          type="number"
        />
      </div>
      <div>
        {maxDiscountedUnits !== null && (
          <Typography variant="caption" className="text-xs font-medium text-gray-500">
            {t('entities.contract_line.available_discounted_units')}:{' '}
            <span className="font-semibold text-primary-600">{maxDiscountedUnits}</span>
          </Typography>
        )}
        <IdentityField
          name="discounted_quantity"
          component={TextFieldAdapter}
          label={t('global.discounted_quantity')}
          placeholder={t('global.discounted_quantity')}
          type="number"
        />
      </div>
      {!programmed && (
        <IdentityField
          gridSpan={2}
          name="requested_shipment_date"
          component={DatePickerAdapter}
          initialValue={addBusinessDays(dayjs(), 5).format('YYYY-MM-DD')}
          label="Requested shipment date"
          type="date"
        />
      )}

      {/* Ripetizione */}
      <OrderFrequencyFields programmed={programmed} />

      {/* Altro/note */}
      <OrderNotesField selectedContractLine={selectedContractLine} />
    </EnhancedForm>
  );
};

export default PurchaseRequestLineForm;
